import type { Draft } from 'immer';

export function isPresent<T>(t: T | undefined | null | void): t is T {
  return t !== undefined && t !== null;
}

export function isDefined<T>(t: T | undefined): t is T {
  return t !== undefined;
}

export function isFilled<T>(t: T | null): t is T {
  return t !== null;
}

export function isNotFalse<T>(t: T | false): t is T {
  return t !== false;
}

export type UnionKeys<T> = T extends any ? keyof T : never;

export type StrictUnionHelper<T, TAll> = T extends any
  ? T & Partial<Record<Exclude<UnionKeys<TAll>, keyof T>, never>>
  : never;

export type StrictUnion<T> = StrictUnionHelper<T, T>;

export type IsEmptyObject<Obj extends Record<string, unknown>> = [keyof Obj] extends [never]
  ? true
  : false;

export type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };
export type XOR<T, U> = T | U extends Object ? (Without<T, U> & U) | (Without<U, T> & T) : T | U;
export type Rename<T, U> = {
  [K in keyof T as K extends keyof U ? (U[K] extends string ? U[K] : never) : K]: K extends keyof T
    ? T[K]
    : never;
};

// Used for styled component types, prefixing keys in U with '$' to make transient props
export type Transient<T, U extends keyof T = keyof T> = {
  [K in U as `$${K & string}`]: T[K];
};

export type MakePropertiesOptional<Type, Key = keyof Type> = Key extends keyof Type
  ? Omit<Type, Key> & Partial<Pick<Type, Key>>
  : never;

export type MakePropertiesRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

/**
 * Returns the union of keys that have type `Type` in the object `Obj`.
 * type Something = {
 *   dude: string;
 *   sweet: number;
 *   amazing: number;
 * }
 * type Keys = KeysOfValueType<Something, number>;
 * //   Keys = "sweet" | "amazing";
 */
export type KeysOfValueType<Obj, Type> = {
  [K in keyof Obj]: Obj[K] extends Type ? K : never;
}[keyof Obj];

export type AnyFn = (...params: any[]) => void;

export type ImmerUpdateFn<T> = (state: Draft<T>) => void;

export type PickLiteral<A, B extends A> = B;

export type Timeout = ReturnType<typeof setTimeout>;

export type BooleanKey<T> = Extract<KeysOfValueType<T, boolean>, string>;

// This helps typescript do exhaustive switch case matching.
// A default value can be returned just in case the runtime
// code actually runs through to the default case for some reason.
export function unreachable(x: never, defaultValue: any): never {
  // @ts-ignore
  return defaultValue;
}
